/**
 * The built-in resolver that turns tool-emitted {@link FlowSignal}s into
 * request transforms.
 *
 * A tool steers the next turn by placing a `flowSignal` field on its result
 * output. This resolver reads the LATEST settled tool part from the transcript,
 * extracts and validates that signal, and compiles each present field into the
 * corresponding {@link TurnTransform} via the shared combinators — so signal
 * semantics and `flow()` semantics stay one and the same.
 */

import type { CallSettings, ModelMessage } from 'ai';
import { z } from 'zod';

import {
  appendMessages,
  forceTool,
  narrowTools,
  noTool,
  prependMessages,
  requireTool,
  withCallSettings,
  withSystem,
} from './transforms.ts';
import type { FlowOutput, FlowResolverEntry, FlowSignal, ReadonlyToolPart, TurnTransform } from './types.ts';

/** Default priority for the built-in signal resolver. */
const DEFAULT_SIGNAL_PRIORITY = 0;

/**
 * Validates the serializable surface of a {@link FlowSignal}. The message and
 * call-settings payloads originate inside tool code (a trusted producer) and
 * are AI SDK structural types, so they are accepted opaquely rather than
 * re-modelled here; the discriminating primitive fields are checked strictly.
 */
const FlowSignalSchema = z
  .object({
    forceTool: z.string().optional(),
    requireTool: z.boolean().optional(),
    noTool: z.boolean().optional(),
    narrowTools: z.array(z.string()).optional(),
    appendMessages: z.array(z.custom<ModelMessage>()).optional(),
    prependMessages: z.array(z.custom<ModelMessage>()).optional(),
    system: z.string().optional(),
    callSettings: z.custom<Partial<CallSettings>>().optional(),
    continuation: z.enum(['continue', 'stop']).optional(),
    replacedByNextCall: z.boolean().optional(),
  })
  .strict();

/** Read a `flowSignal` field off a settled tool part's output, if present. */
function readSignal(part: ReadonlyToolPart | undefined): FlowSignal | undefined {
  if (!part || typeof part.output !== 'object' || part.output === null || !('flowSignal' in part.output)) {
    return undefined;
  }
  const parsed = FlowSignalSchema.safeParse((part.output as { flowSignal: unknown }).flowSignal);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Lookup of signal field → transform factory. Each entry receives the signal's
 * value for that field (already known to be present) and returns a transform.
 * Iterated over the present keys to build the transform list — no branching.
 */
const SIGNAL_TRANSFORMS: {
  [FieldType in keyof FlowSignal]-?: (value: NonNullable<FlowSignal[FieldType]>) => TurnTransform | undefined;
} = {
  forceTool: (name) => forceTool(name),
  requireTool: (on) => (on ? requireTool() : undefined),
  noTool: (on) => (on ? noTool() : undefined),
  narrowTools: (names) => narrowTools(names),
  appendMessages: (msgs) => appendMessages(msgs),
  prependMessages: (msgs) => prependMessages(msgs),
  system: (system) => withSystem(system),
  callSettings: (settings) => withCallSettings(settings),
  continuation: () => undefined,
  // Consumed at the <Tool> execute boundary (forcedReplacementTarget), not
  // as a request transform — like `continuation`, it steers outside the
  // TurnRequest.
  replacedByNextCall: () => undefined,
};

/** Compile a validated signal into the ordered transforms its fields imply. */
function compileSignal(signal: FlowSignal): TurnTransform[] {
  return Object.entries(signal).flatMap(([field, value]) => {
    if (value === undefined) {
      return [];
    }
    const make = SIGNAL_TRANSFORMS[field as keyof FlowSignal] as
      | ((value: unknown) => TurnTransform | undefined)
      | undefined;
    const transform = make?.(value);
    return transform ? [transform] : [];
  });
}

/**
 * Build the built-in {@link FlowResolverEntry} that compiles the latest tool
 * result's {@link FlowSignal} into transforms and surfaces its continuation.
 * Returns an empty output when the latest tool part carries no signal.
 */
export function signalResolver(priority: number = DEFAULT_SIGNAL_PRIORITY): FlowResolverEntry {
  return {
    priority,
    resolve({ transcript }): FlowOutput {
      const signal = readSignal(transcript.latestToolPart());
      if (!signal) {
        return { transforms: [] };
      }
      return { transforms: compileSignal(signal), continuation: signal.continuation };
    },
  };
}
